/* ============================================================
   DEALER PRICING — shows trade pricing to approved dealers.

   Any price rendered with data-dealer-price="<cents>" shows the trade price
   in place of retail. The attribute carries the base price from Liquid so
   nothing has to be parsed out of formatted text.

   A dealer's page is deliberately identical to a retail visitor's: one price,
   in the theme's own markup and styling. The ONLY difference is a green
   "DEALERS" label in front of the price on the product page — the price itself
   stays the theme's black. Earlier revisions painted a
   two-row DEALER/RETAIL block with saving notes; Soare rejected it — do not
   reintroduce a second price row here. A struck comparison price, if wanted,
   belongs on the B2B price list as compareAtPrice, which the theme's existing
   compare-at markup renders on its own.

   Dealer status comes from the TSeller B2B Backend app over the /apps/dealer
   app proxy. The customer's Customer Account API token is sent in the body
   because portal sign-in creates no storefront session; the app verifies it.
   ============================================================ */

(() => {
  const TOKEN_KEY = 'acctPortal:accessToken';
  const DEALER_API = '/apps/dealer';
  const API = DEALER_API + '/status';
  const CACHE_KEY = 'dealerPricing:status';
  const DEALER_TOKEN_KEY = 'dealerPricing:dealerToken';
  const CACHE_MS = 5 * 60 * 1000;

  const token = () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  };

  // Comma thousands to match the theme's own formatting — en-ZA would render
  // "R 4 499" against the theme's "R 4,999", which reads as a different currency.
  const rand = (cents) =>
    'R ' + String(Math.round(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // "DEALERS" in front of the product page's price — the one and only visual
  // difference between what a dealer sees and what a retail visitor sees.
  // Fixed wording, deliberately not the buyer's tier name: it labels the price
  // list this figure comes from, so an Installer reads "DEALERS" too.
  // Idempotent: the observer re-applies on variant switches.
  function paintDealerTag(node) {
    const prev = node.previousElementSibling;
    if (prev && prev.classList.contains('dealer-tag')) return;
    const tag = document.createElement('span');
    tag.className = 'dealer-tag';
    tag.textContent = 'Dealers'; // uppercased in CSS
    node.insertAdjacentElement('beforebegin', tag);
  }

  // The cart renders money with decimals; matching it keeps the page identical.
  const randCents = (cents) => {
    const whole = Math.floor(cents / 100);
    const frac = String(cents % 100).padStart(2, '0');
    return 'R ' + String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + frac;
  };

  function cached() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { at, data } = JSON.parse(raw);
      return Date.now() - at < CACHE_MS ? data : null;
    } catch (e) {
      return null;
    }
  }

  function cache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
    } catch (e) {
      /* private mode — just re-fetch next page */
    }
  }

  /**
   * Dev-only override. The account portal's OAuth callback is registered
   * against the canonical domain, so you cannot sign in on the theme dev
   * server — which makes dealer styling impossible to work on locally.
   *
   * Hard-gated to localhost: on any real domain this returns null before
   * reading anything, so it cannot be triggered in production.
   *
   *   ?dealer_preview=1    on, at the default 10%
   *   ?dealer_preview=25   on, at 25%
   *   ?dealer_preview=0    off
   */
  function devOverride() {
    const host = location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') return null;

    const KEY = 'dealerPricing:devPreview';
    const param = new URLSearchParams(location.search).get('dealer_preview');

    if (param !== null) {
      if (param === '0' || param === 'false') {
        sessionStorage.removeItem(KEY);
        return null;
      }
      const pct = Number(param) > 1 ? Number(param) : 10;
      sessionStorage.setItem(KEY, String(pct));
    }

    const stored = Number(sessionStorage.getItem(KEY));
    if (!stored) return null;

    return {
      signedIn: true,
      isDealer: true,
      status: 'approved',
      pricingUnlocked: true,
      tier: { handle: 'dealer', name: 'Dealer', percentOff: stored },
    };
  }

  const dealerToken = () => {
    try {
      return localStorage.getItem(DEALER_TOKEN_KEY);
    } catch (e) {
      return null;
    }
  };

  function dealerFetch(path, extra) {
    const t = token();
    const d = dealerToken();
    // A B2B storefront session needs no token at all: Shopify signs
    // logged_in_customer_id into the proxied request itself.
    const b2b = document.documentElement.classList.contains('is-dealer-b2b');
    if (!t && !d && !b2b) return Promise.resolve(null);
    return fetch(DEALER_API + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ token: t, dealerToken: d }, extra || {})),
    });
  }

  async function fetchStatus() {
    const dev = devOverride();
    if (dev) return dev;

    const t = token();
    if (!t) return null;

    const hit = cached();
    if (hit) return hit;

    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: t }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      cache(data);
      return data;
    } catch (e) {
      // Never let a pricing outage break the page — retail simply stands.
      return null;
    }
  }

  // Stored trade prices, keyed by numeric variant id. Cached for the session so
  // navigating between pages resolves instantly instead of re-fetching.
  const BOOK_KEY = 'dealerPricing:book4'; // bumped 2026-08-04: flat 10% replaced by fixed IQ wholesale prices
  // One persisted book per variant: {n: trade cents|null, r: retail cents|null}.
  // Both halves must persist together — caching only the trade price meant
  // revisited pages never fetched retail, and the RETAIL row fell back to the
  // page's own price, which under B2B context IS the trade price.
  // localStorage, not sessionStorage: dealers open product links in new tabs,
  // and a per-tab cache made every one of them a cold start. Entries carry a
  // timestamp and expire after six hours.
  const BOOK_TTL_MS = 6 * 60 * 60 * 1000;
  const priceBook = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem(BOOK_KEY) || '{}');
      const now = Date.now();
      const out = {};
      for (const k of Object.keys(raw)) {
        const e = raw[k];
        if (e && typeof e === 'object' && now - (e.at || 0) < BOOK_TTL_MS) out[k] = e;
      }
      return out;
    } catch (e) {
      return {};
    }
  })();

  function persistBook() {
    try {
      localStorage.setItem(BOOK_KEY, JSON.stringify(priceBook));
    } catch (e) {
      /* private mode */
    }
  }

  async function loadPrices(nodes) {
    const ids = [];
    nodes.forEach((n) => {
      const v = n.getAttribute('data-dealer-variant');
      if (v && !(v in priceBook)) ids.push('gid://shopify/ProductVariant/' + v.replace(/\D/g, ''));
    });
    if (!ids.length) return;

    try {
      const res = await dealerFetch('/prices', { variantIds: ids });
      if (!res || !res.ok) return;
      const data = await res.json();
      ids.forEach((gid) => {
        const num = gid.replace(/\D/g, '');
        priceBook[num] = {
          n: (data.prices || {})[gid] ?? null,
          r: (data.retail || {})[gid] ?? null,
          at: Date.now(),
        };
      });
      persistBook();
    } catch (e) {
      /* retail stands */
    }
  }

  function apply(percentOff) {
    const nodes = document.querySelectorAll('[data-dealer-price]:not([data-dealer-applied])');
    nodes.forEach((node) => {
      const cents = Number(node.getAttribute('data-dealer-price'));
      if (!Number.isFinite(cents) || cents <= 0) return;

      const variant = (node.getAttribute('data-dealer-variant') || '').replace(/\D/g, '');
      const entry = variant ? priceBook[variant] : undefined;

      const b2bMode = document.documentElement.classList.contains('is-dealer-b2b');
      const context = node.getAttribute('data-dealer-context');

      if (b2bMode) {
        // Native B2B: Liquid already rendered every price in the buyer's
        // catalog context, so the figure on the page IS what checkout charges —
        // the catalog trade price for a signed dealer, plain retail for one who
        // has not signed yet. Nothing to substitute anywhere, and the app's
        // stored price table must never be painted over it: that would drift
        // from checkout. The only change is the label on the product page.
        node.setAttribute('data-dealer-applied', '');
        if (context === 'product') paintDealerTag(node);
        return;
      }

      if (variant && entry === undefined) {
        // Fallback (portal-token) path: wait for the full lookup.
        return;
      }

      node.setAttribute('data-dealer-applied', '');
      const stored = entry ? entry.n : null;
      const serverRetail = entry ? entry.r : null;

      // Cart lines show a line total, so a stored unit price is multiplied by
      // the line quantity before it is displayed.
      const qty = Number(node.getAttribute('data-dealer-qty')) || 1;

      // Source of truth for the dealer figure: when native B2B has
      // contextualised this page, the price Liquid rendered here (`cents`) IS
      // the catalog trade price — use it, so display can never drift from
      // what checkout charges. Fall back to the stored table otherwise.
      const catalogActive = serverRetail && cents < serverRetail;
      const dealerCents = catalogActive
        ? cents * qty
        : stored
          ? stored * qty
          : percentOff > 0
            ? Math.round(cents * (1 - percentOff / 100))
            : cents * qty;

      // The cart stays visually identical to a retail cart — the dealer figure
      // simply replaces the price, rather than showing both.
      if (context === 'cart') {
        node.textContent = randCents(dealerCents);
        node.setAttribute('data-dealer-line-cents', String(dealerCents));
        return;
      }

      // Every other context — product page, collection cards, carousels, the
      // mobile sticky bar — is a single price in the theme's own markup. The
      // trade figure simply replaces it, so the page stays visually identical
      // to a retail visitor's.
      node.textContent = rand(dealerCents);
      if (context === 'product') paintDealerTag(node);
    });

    updateCartTotal();
  }

  /**
   * Sums the dealer line totals so the cart total matches the lines above it.
   * Only touches the total once every line has a dealer figure — a partial sum
   * would be worse than leaving retail in place.
   */
  function updateCartTotal() {
    // Native B2B: Liquid rendered cart.total_price in the buyer's catalog
    // context, so the total on the page is already the trade total. Nothing to
    // recalculate — and nothing here may touch it.
    if (document.documentElement.classList.contains('is-dealer-b2b')) return;

    const totals = document.querySelectorAll('[data-dealer-total]');
    if (!totals.length) return;

    const lines = document.querySelectorAll('[data-dealer-context="cart"]');
    if (!lines.length) return;

    // Each cart line renders twice — a desktop row and a mobile row, one hidden
    // by CSS — so sum per line key rather than per element.
    const byLine = {};
    for (const line of lines) {
      // A line that was skipped has no attribute at all. Number(null) is 0, so
      // reading it straight would silently price the line at zero and write a
      // R 0.00 total over a correct one — check for the attribute itself.
      const raw = line.getAttribute('data-dealer-line-cents');
      if (raw === null) return; // not all resolved yet
      const cents = Number(raw);
      if (!Number.isFinite(cents)) return;
      byLine[line.getAttribute('data-dealer-line') || line.getAttribute('data-dealer-variant') || ''] = cents;
    }

    const sum = Object.keys(byLine).reduce((a, k) => a + byLine[k], 0);

    totals.forEach((t) => {
      if (t.getAttribute('data-dealer-total-cents') === String(sum)) return;
      t.setAttribute('data-dealer-total-cents', String(sum));
      t.textContent = randCents(sum);
    });
  }


  async function init() {
    // Liquid already told us this visitor is a B2B buyer, and every price it
    // rendered is already the buyer's catalog price. Nothing to look up and
    // nothing to fetch — the app is not in the critical path at all, so the
    // label is the whole job and it happens synchronously.
    if (document.documentElement.classList.contains('is-dealer-b2b')) {
      // Liquid gates the dealer classes on the b2b market — the same thing
      // that delivers catalog prices — so a visitor reaching this path is a
      // signed dealer and paints immediately with no round trip. The status
      // check below is belt and braces only, and runs AFTER the paint: if the
      // app says the agreement is not signed, strip the dealer dressing.
      const verifyEntitlement = async () => {
        let entitlement = cached();
        if (!entitlement) {
          try {
            const res = await dealerFetch('/status');
            if (res && res.ok) {
              entitlement = await res.json();
              cache(entitlement);
            }
          } catch (e) {
            /* status outage — the catalog price is authoritative anyway */
          }
        }
        if (entitlement && !entitlement.pricingUnlocked) {
          document.documentElement.classList.remove('is-dealer', 'is-dealer-b2b', 'is-dealer-loading');
          document.documentElement.classList.add('is-dealer-revealed');
          document.querySelectorAll('.dealer-tag, .dealer-save-note').forEach((n) => n.remove());
        }
      };
      const run = () => apply(0);
      // Never leave prices hidden if something above goes wrong.
      setTimeout(
        () => document.documentElement.classList.add('is-dealer-revealed'),
        3000,
      );
      run();
      document.documentElement.classList.remove('is-dealer-loading');
      verifyEntitlement();
      let queued = false;
      const obs = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        setTimeout(() => {
          queued = false;
          run();
          document.documentElement.classList.remove('is-dealer-loading');
        }, 60);
      });
      obs.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-dealer-price', 'data-dealer-variant'],
      });
      return;
    }

    const status = await fetchStatus();
    if (!status || !status.pricingUnlocked) {
      document.documentElement.classList.remove('is-dealer-loading');
      return;
    }

    const percentOff = status.tier && status.tier.percentOff;
    if (!percentOff || percentOff <= 0) {
      document.documentElement.classList.remove('is-dealer-loading');
      return;
    }

    document.documentElement.classList.add('is-dealer');

    // Unprocessed prices are hidden while trade prices resolve; this guarantees
    // they become visible again even if a lookup never returns.
    setTimeout(
      () => document.documentElement.classList.add('is-dealer-revealed'),
      3000,
    );

    let chain = Promise.resolve();
    const run = () => {
      chain = chain.then(async () => {
        const pending = document.querySelectorAll('[data-dealer-price]:not([data-dealer-applied])');
        if (!pending.length) return;
        await loadPrices(pending);
        apply(percentOff);
      });
      return chain;
    };

    await run();
    document.documentElement.classList.remove('is-dealer-loading');

    // Prices are re-rendered on variant change, filtering and paginated loads.
    let queued = false;
    const obs = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        run().then(() => document.documentElement.classList.remove('is-dealer-loading'));
      }, 60);
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      // Variant switchers rewrite these in place rather than replacing the
      // node, so attribute changes have to trigger a re-apply too.
      attributes: true,
      attributeFilter: ['data-dealer-price', 'data-dealer-variant'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
